import { useEffect } from 'react';
import { darkTheme, RainbowKitProvider } from '@rainbow-me/rainbowkit';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { observer } from 'mobx-react-lite';

import 'styles/index.scss';
import { Notifications } from 'components';
import { CT_CHAIN_ID } from 'consts/copyTrading';
import { Main } from 'containers';
import { useDisconnect, useHandleConnectClick } from 'hooks';
import { store } from 'store';
import { GlobalStyles } from 'styles/global';
import { WalletId } from 'types';
import { useAccount, useChainId, WagmiProvider } from 'wagmi';

import { WALLETS_IDS } from './connectors';
import { web3Config } from './web3Config';

import '@rainbow-me/rainbowkit/styles.css';

const queryClient = new QueryClient();

const Web3Sync = observer(() => {
  const { syncWeb3Data, setChainId } = store.web3Store;

  const { address, connector } = useAccount();
  const chainId = useChainId();

  useEffect(() => {
    const connectorId = connector?.id;
    const walletId: WalletId | undefined =
      connectorId && WALLETS_IDS.includes(connectorId as WalletId)
        ? (connectorId as WalletId)
        : connectorId
          ? (localStorage.getItem('rk-latest-id') as WalletId) || undefined
          : undefined;

    syncWeb3Data({ account: address, walletId });
  }, [address, connector, syncWeb3Data]);

  useEffect(() => {
    setChainId(chainId);
  }, [chainId, setChainId]);

  return null;
});

/** Registers React-hook-dependent actions into AssistantStore.actions */
const AssistantActionSync = () => {
  const { handleConnectClick } = useHandleConnectClick();
  const { disconnect } = useDisconnect();

  useEffect(() => {
    store.assistantStore.setAction('connect_wallet', handleConnectClick);
  }, [handleConnectClick]);

  useEffect(() => {
    store.assistantStore.setAction('disconnect_wallet', disconnect);
  }, [disconnect]);

  return null;
};

export const App = observer(() => {
  return (
    <>
      <GlobalStyles />
      <WagmiProvider config={web3Config}>
        <QueryClientProvider client={queryClient}>
          <RainbowKitProvider
            theme={darkTheme({
              accentColor: '#6366f1',
              borderRadius: 'medium',
            })}
            initialChain={CT_CHAIN_ID}
            modalSize="compact"
            locale="en"
          >
            <Web3Sync />
            <AssistantActionSync />
            <Notifications />
            <Main />
          </RainbowKitProvider>
        </QueryClientProvider>
      </WagmiProvider>
    </>
  );
});
